import AuthApi from '@apis/auth/AuthApi';
import {
    ACCESS_TOKEN_MAX_AGE_SECOND,
    REFRESH_TOKEN_MAX_AGE_SECOND,
} from '@constants/auth';
import useIdentification from '@hooks/auth/useIdentification';
import useUser from '@hooks/auth/useUser';
import useToast from '@hooks/ui/useToast';
import useProject from '@hooks/useProject';
import useProjectId from '@hooks/useProjectId';
import { AppleSignInError } from '@models/auth';
import { NotVerificationSignInErrorSchema } from '@models/auth/schema';
import type { RegisteredActivityName } from '@stackflow/config';
import { useFlow } from '@stackflow/react';
import { useMutation } from '@tanstack/react-query';
import { pushAuthEvent, savePendingAuthEvent } from '@utils/authEventUtils';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

interface IFlow {
  activityName: RegisteredActivityName,
  activityParams: object,
}

function useSignInWithApple(code: string) {
    const projectId = useProjectId();
    const [cookies, setCookies, removeCookies] = useCookies(['accessToken', 'refreshToken', 'authFlow'], {
        doNotParse: true,
    });
    const { replace, push } = useFlow();
    const toast = useToast();
    const { t } = useTranslation('common');
    const { getUserProfile } = useUser();
    const project = useProject();
    const handleIdentification = useIdentification();
    const { mutate } = useMutation({
        mutationFn: project?.serviceCountry === 'KR'
            ? AuthApi.oauthSIgnIn : AuthApi.oauthSignInWithEmail,
        onSuccess(data) {
            const { token, refreshToken } = data;
            setCookies('accessToken', token, {
                path: '/',
                maxAge: ACCESS_TOKEN_MAX_AGE_SECOND,
            });
            setCookies('refreshToken', refreshToken, {
                path: '/',
                maxAge: REFRESH_TOKEN_MAX_AGE_SECOND,
            });
            getUserProfile();
            window.localStorage.setItem('lastLoginMethod', 'APPLE');
            pushAuthEvent('login', 'APPLE');
            try {
                const parsed: IFlow[] = JSON.parse(cookies.authFlow);
                replace('MainActivity', {}, { animate: false });
                for (let i = 0; i < parsed.length; i += 1) {
                    const flow = parsed[i];
                    push(flow.activityName, flow.activityParams, { animate: i + 1 === parsed.length });
                }
                removeCookies('authFlow');
            } catch (e) {
                console.error(e);
                replace('MainActivity', {});
            }
        },
        onError(e: AxiosError<AppleSignInError>) {
            if (e.response?.status === 401) {
                replace('MainActivity', {}, { animate: false });
                push('SignInActivity', {}, { animate: false });
                push('SignUpActivity', {
                    appleId: e.response?.data.data.appleId,
                    verificationToken: e.response?.data?.data?.verificationToken,
                    email: e.response?.data?.data?.email,
                });
            } else {
                const parsed = NotVerificationSignInErrorSchema.safeParse(e.response?.data);
                if (parsed.success) {
                    toast.error('본인인증이 필요합니다.');
                    savePendingAuthEvent('login', 'APPLE');
                    replace('MainActivity', {}, { animate: false });
                    push('SignInActivity', {}, { animate: false });
                    handleIdentification({
                        projectId,
                        verificationToken: parsed.data.data.verificationToken,
                        marketingAgreed: false,
                        locationAgreed: false,
                    });
                } else {
                    toast.error(t('oauthSignInFailed'));
                    replace('MainActivity', {}, { animate: false });
                    push('SignInActivity', {});
                }
            }
        },
    });
    useEffect(() => {
        if (project) {
            mutate({ code, projectId, signUpType: 'APPLE' });
        }
    }, [projectId, code, project]);
}

export default useSignInWithApple;
